import { useEffect, useState } from "react";
import { Card, Checkbox, Cluster, Input, Select, Stack } from "@salesapp/ui";
import { FIELD_HINTS } from "../../help/fieldHints";
import { opsApi, type CompanyLifecycleSettings, type CycleTemplate } from "../../lib/opsApi";

const EMPTY: CompanyLifecycleSettings = {
  watch_leads: true,
  watch_opportunities: true,
  watch_quotes: true,
  watch_orders: true,
  watch_invoices: true,
  dormant_enabled: false,
  dormant_after_days: 90,
  inactive_enabled: false,
  inactive_after_days: 180,
  winback_enabled: false,
  winback_template_id: null,
};

type WatchKey = "watch_leads" | "watch_opportunities" | "watch_quotes" | "watch_orders" | "watch_invoices";

const WATCH: Array<{ key: WatchKey; label: string }> = [
  { key: "watch_leads", label: "Leads" },
  { key: "watch_opportunities", label: "Opportunities" },
  { key: "watch_quotes", label: "Quotes" },
  { key: "watch_orders", label: "Orders" },
  { key: "watch_invoices", label: "Invoices" },
];

function problem(next: CompanyLifecycleSettings): string | null {
  const watching = WATCH.some((item) => next[item.key]);
  if ((next.dormant_enabled || next.inactive_enabled) && !watching) {
    return "Choose at least one tab to watch, or turn both rules off.";
  }
  if (next.dormant_enabled && next.inactive_enabled && next.inactive_after_days <= next.dormant_after_days) {
    return "Inactive needs a longer quiet period than dormant. Raise the inactive days, or turn one rule off.";
  }
  return null;
}

export function CompanyLifecycleSettings({ orgId }: { orgId: string }) {
  const [settings, setSettings] = useState<CompanyLifecycleSettings>(EMPTY);
  const [templates, setTemplates] = useState<CycleTemplate[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void opsApi.getCompanyLifecycle(orgId).then((row) => {
      if (!cancelled) setSettings(row);
    }).catch((err) => {
      if (!cancelled) setError(err instanceof Error ? err.message : "Status rules could not be loaded.");
    });
    void opsApi.listCycleTemplates(orgId).then((row) => {
      if (!cancelled) setTemplates(row.templates);
    }).catch(() => {
      if (!cancelled) setTemplates([]);
    });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  function update(patch: Partial<CompanyLifecycleSettings>) {
    const next = { ...settings, ...patch };
    setSettings(next);
    const message = problem(next);
    setError(message);
    if (message) return;
    void opsApi.saveCompanyLifecycle(orgId, next).then(setSettings).catch((err) => {
      setError(err instanceof Error ? err.message : "Status rules could not be saved.");
    });
  }

  function setDays(key: "dormant_after_days" | "inactive_after_days", raw: string) {
    const next = Number(raw);
    if (!Number.isFinite(next) || next < 1 || next > 3650) return;
    update({ [key]: next });
  }

  return (
    <Card>
      <Stack gap={3}>
        <h2 className="crm-section-title">Account status rules</h2>
        <p className="home-subtitle">
          Dormant means the account has gone quiet. Inactive means you are no longer working it.
          A salesperson can change either status on the company at any time. That manual change restarts the quiet clock,
          so these rules do not immediately undo it. Both rules stay off until you turn them on. They run once a day.
        </p>
        {error ? <p className="crm-error">{error}</p> : null}
        <p className="home-subtitle">Count activity on these tabs. The clock uses the newest record among the ones you tick.</p>
        <Cluster gap={3}>
          {WATCH.map((item) => (
            <Checkbox
              key={item.key}
              checked={Boolean(settings[item.key])}
              onChange={(event) => {
                const patch: Partial<CompanyLifecycleSettings> = {};
                patch[item.key] = event.target.checked;
                update(patch);
              }}
              label={item.label}
              tooltip={FIELD_HINTS.lifecycle_watch}
            />
          ))}
        </Cluster>
        <Cluster gap={3} align="center">
          <Checkbox
            checked={settings.dormant_enabled}
            onChange={(event) => update({ dormant_enabled: event.target.checked })}
            label="Mark dormant after"
            tooltip={FIELD_HINTS.lifecycle_dormant_days}
          />
          <Input
            type="number"
            aria-label="Days until dormant"
            tooltip={FIELD_HINTS.lifecycle_dormant_days}
            value={String(settings.dormant_after_days)}
            onChange={(event) => setDays("dormant_after_days", event.target.value)}
          />
          <span className="crm-muted">days with nothing new</span>
        </Cluster>
        <Cluster gap={3} align="center">
          <Checkbox
            checked={settings.inactive_enabled}
            onChange={(event) => update({ inactive_enabled: event.target.checked })}
            label="Mark inactive after"
            tooltip={FIELD_HINTS.lifecycle_inactive_days}
          />
          <Input
            type="number"
            aria-label="Days until inactive"
            tooltip={FIELD_HINTS.lifecycle_inactive_days}
            value={String(settings.inactive_after_days)}
            onChange={(event) => setDays("inactive_after_days", event.target.value)}
          />
          <span className="crm-muted">days with nothing new</span>
        </Cluster>
        <p className="home-subtitle">
          Only active and prospect companies become dormant. Inactive also covers a company that is already dormant.
          Incomplete and merged companies are left alone. A dormant company the rule set goes back to active when a watched record is added.
          A status someone chose by hand stays until they change it or the quiet period passes again.
        </p>
        <h2 className="crm-section-title">When a company becomes inactive</h2>
        <Checkbox
          checked={settings.winback_enabled}
          onChange={(event) => update({ winback_enabled: event.target.checked })}
          label="Start a sales cycle"
          tooltip={FIELD_HINTS.lifecycle_winback}
        />
        <Select
          label="Sales cycle"
          tooltip={FIELD_HINTS.lifecycle_template}
          value={settings.winback_template_id ?? ""}
          onChange={(event) => update({ winback_template_id: event.target.value || null })}
        >
          <option value="">Standard sales cycle</option>
          {templates.map((template) => (
            <option key={template.id} value={template.id}>{template.name}</option>
          ))}
        </Select>
        <p className="home-subtitle">
          Optional. Leave this off and the status still changes, with no cycle. The stages and tasks — rediscover the
          account, refresh KYC, and position current products — are edited on the Sales cycle template, not here.
          An account that already has a cycle in progress is not given a second one.
        </p>
      </Stack>
    </Card>
  );
}
